import { useEffect, useState } from 'react';
import { getStoredToken, postLog } from '../utils/authApi';
import * as XLSX from 'xlsx';
import { getCurrencyDisplay } from '../utils/currency';
import { getPermissions } from '../utils/permissions';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

type Period = 'day' | 'week' | 'month' | 'year';
const PERIODS: Period[] = ['day', 'week', 'month', 'year'];
const PERIOD_LABEL: Record<Period, string> = { day: 'Today', week: 'This Week', month: 'This Month', year: 'This Year' };

interface ProductEntry {
  id: number;
  name: string;
  category: string;
  stock_quantity: number;
  low_stock_threshold: number;
  is_negative: boolean;
  returned: number;
  damaged: number;
  sold: Record<Period, number>;
  revenue: Record<Period, number>;
}

interface ReportData {
  sales_count: Record<Period, number>;
  revenue: Record<Period, number>;
  top_products: ProductEntry[];
  all_stock: ProductEntry[];
  low_stock_alerts: ProductEntry[];
}

// ── Log Return/Damage Modal ───────────────────────────────────────
function LogMovementModal({ product, onClose, onDone }: {
  product: ProductEntry;
  onClose: () => void;
  onDone: () => void;
}) {
  const [type, setType] = useState<'return' | 'damage'>('return');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (qty <= 0) return setErr('Quantity must be > 0');
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${BACKEND}/inventory/stock-movement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getStoredToken()}`,
        },
        body: JSON.stringify({ product_id: product.id, movement_type: type, quantity: qty, note }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      postLog(
        type === 'return' ? 'INVENTORY_RETURN_LOGGED' : 'INVENTORY_DAMAGE_LOGGED',
        `${type === 'return' ? 'Return' : 'Damage'} logged: ${product.name} | Qty: ${qty}${note.trim() ? ` | Note: ${note.trim()}` : ''}`,
      );
      onDone();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Log Movement</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>{product.name} — current stock: <b>{product.stock_quantity}</b></div>

        {/* Type toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {(['return', 'damage'] as const).map(t => (
            <button key={t} onClick={() => setType(t)} style={{
              flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
              background: type === t ? (t === 'return' ? '#dcfce7' : '#fef2f2') : '#f1f5f9',
              color: type === t ? (t === 'return' ? '#16a34a' : '#ef4444') : '#94a3b8',
            }}>
              {t === 'return' ? '↩ Return' : '⚠ Damage'}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
          {type === 'return' ? '✅ Stock will increase by this quantity' : '❌ Stock will decrease by this quantity'}
        </div>

        {/* Qty */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Quantity</div>
          <input
            type="number" min={1} value={qty}
            onChange={e => setQty(Number(e.target.value))}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>

        {/* Note */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Note (optional)</div>
          <input
            type="text" value={note} placeholder="e.g. customer return, broken in transit"
            onChange={e => setNote(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>

        {err && <div style={{ color: '#ef4444', fontSize: 11, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={loading} style={{
            flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            background: type === 'return' ? '#16a34a' : '#ef4444', color: '#fff',
          }}>
            {loading ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function InventoryReportsTab({
 refreshKey,
  userRole,
 authUserPerms,
 plan,
  planTier,
  planStatus,
  moduleStatuses,
  outletId,
}: {
 refreshKey?: number;
  userRole?: string;
 authUserPerms?: Record<string, boolean>;
 plan?: string;
  planTier?: string;
  planStatus?: string;
  moduleStatuses?: any;
  outletId?: number | null;
}) {
  const [data, setData] = useState<ReportData | null>(null);
  const [period, setPeriod] = useState<Period>('month');
  const [logProduct, setLogProduct] = useState<ProductEntry | null>(null);
  const [search, setSearch] = useState('');
  const perms = getPermissions(userRole, authUserPerms, plan, planTier, planStatus, moduleStatuses);
   if (!perms.canManageInventory) {
    return (
      <div style={{ padding: 24, color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
        You don't have permission to view inventory reports.
      </div>
    );
  }
const displayMode = (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol';
const currSym = getCurrencyDisplay(localStorage.getItem('cloudairambo_active_currency') || 'INR', displayMode);
  const fetchData = (silent = false) => {
    if (!silent) setData(null);
    const qs = outletId ? `?outlet_id=${outletId}` : '';
    fetch(`${BACKEND}/inventory/reports${qs}`, {
      headers: { Authorization: `Bearer ${getStoredToken()}` },
    })
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  };

  useEffect(() => { fetchData(); }, [refreshKey, outletId]);

  // Silent background sync
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 15000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchData(true); };
    const onFocus = () => fetchData(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [outletId]);

  if (!data) return <div style={{ padding: 24, color: '#94a3b8', fontSize: 13 }}>Loading reports...</div>;

  const filtered = data.all_stock.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  );

  // ── Exports ──────────────────────────────────────────────────────
  const exportExcel = () => {
  const wb = XLSX.utils.book_new();

  // Sheet 1 - Summary
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
    { Period: 'Today', Sales: data.sales_count.day, [`Revenue (${currSym})`]: data.revenue.day.toFixed(2) },
    { Period: 'This Week', Sales: data.sales_count.week, [`Revenue (${currSym})`]: data.revenue.week.toFixed(2) },
    { Period: 'This Month', Sales: data.sales_count.month, [`Revenue (${currSym})`]: data.revenue.month.toFixed(2) },
    { Period: 'This Year', Sales: data.sales_count.year, [`Revenue (${currSym})`]: data.revenue.year.toFixed(2) },
  ]), 'Summary');

  // Sheet 2 - All Products (full data)
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.all_stock.map(p => ({
      'Product': p.name,
      'Category': p.category || '-',
      'Current Stock': p.stock_quantity,
      'Low Stock Threshold': p.low_stock_threshold,
      'Status': p.is_negative ? 'Negative' : p.stock_quantity <= p.low_stock_threshold ? 'Low' : 'OK',
      // Sales per period
      'Sold Today': p.sold.day,
      'Sold This Week': p.sold.week,
      'Sold This Month': p.sold.month,
      'Sold This Year': p.sold.year,
      // Revenue per period
      [`Rev Today (${currSym})`]: p.revenue.day.toFixed(2),
      [`Rev This Week (${currSym})`]: p.revenue.week.toFixed(2),
       [`Rev This Month (${currSym})`]: p.revenue.month.toFixed(2),
      [`Rev This Year (${currSym})`]: p.revenue.year.toFixed(2),
      // Unsold = current stock
      'Unsold (Current Stock)': p.stock_quantity > 0 ? p.stock_quantity : 0,
      // Returns & Damages
      'Total Returned': p.returned,
      'Total Damaged': p.damaged,
    }))
  ), 'All Products');

  // Sheet 3 - Top Products (per period breakdown)
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.top_products.map((p, i) => ({
      'Rank': i + 1,
      'Product': p.name,
      'Category': p.category || '-',
      'Current Stock': p.stock_quantity,
      'Sold Today': p.sold.day,
      [`Rev Today (${currSym})`]: p.revenue.day.toFixed(2),
      'Sold This Week': p.sold.week,
      [`Rev This Week (${currSym})`]: p.revenue.week.toFixed(2),
      'Sold This Month': p.sold.month,
       [`Rev This Month (${currSym})`]: p.revenue.month.toFixed(2),
      'Sold This Year': p.sold.year,
      [`Rev This Year (${currSym})`]: p.revenue.year.toFixed(2),
      'Total Returned': p.returned,
      'Total Damaged': p.damaged,
    }))
  ), 'Top Products');

  // Sheet 4 - Stock Alerts
  if (data.low_stock_alerts.length > 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.low_stock_alerts.map(p => ({
        'Product': p.name,
        'Category': p.category || '-',
        'Current Stock': p.stock_quantity,
        'Low Stock Threshold': p.low_stock_threshold,
        'Status': p.is_negative ? 'Negative' : 'Low',
        'Sold This Month': p.sold.month,
         [`Rev This Month (${currSym})`]: p.revenue.month.toFixed(2),
        'Sold This Year': p.sold.year,
        [`Rev This Year (${currSym})`]: p.revenue.year.toFixed(2),
        'Total Returned': p.returned,
        'Total Damaged': p.damaged,
      }))
    ), 'Stock Alerts');
  }

  // Sheet 5 - Full Movement Summary (one row per product, all data combined)
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.all_stock.map(p => ({
      'Product': p.name,
      'Category': p.category || '-',
      'Opening Stock (Current)': p.stock_quantity,
      'Total Sold (Year)': p.sold.year,
      [`Total Revenue (Year) ${currSym}`]: p.revenue.year.toFixed(2),
      [`Avg Revenue Per Sale ${currSym}`]: p.sold.year > 0 ? (p.revenue.year / p.sold.year).toFixed(2) : '0.00',
      'Total Returned': p.returned,
      'Total Damaged': p.damaged,
      'Net Movement': p.sold.year - p.returned - p.damaged,
      'Stock Status': p.is_negative ? 'Negative' : p.stock_quantity <= p.low_stock_threshold ? 'Low' : 'OK',
    }))
  ), 'Full Summary');

  XLSX.writeFile(wb, `inventory_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

  const exportCSV = () => {
    const rows: any[] = [
      ['SALES SUMMARY'],
      ['Period', 'Sales', 'Revenue'],
      ['Today', data.sales_count.day, data.revenue.day.toFixed(2)],
      ['This Week', data.sales_count.week, data.revenue.week.toFixed(2)],
      ['This Month', data.sales_count.month, data.revenue.month.toFixed(2)],
      ['This Year', data.sales_count.year, data.revenue.year.toFixed(2)],
      [],
      ['ALL PRODUCTS'],
      ['Product', 'Category', 'Stock', 'Threshold', 'Sold Today', 'Sold Week', 'Sold Month', 'Sold Year', 'Rev Today', 'Rev Week', 'Rev Month', 'Rev Year', 'Returned', 'Damaged', 'Status'],
      ...data.all_stock.map(p => [
        p.name, p.category || '-', p.stock_quantity, p.low_stock_threshold,
        p.sold.day, p.sold.week, p.sold.month, p.sold.year,
        p.revenue.day.toFixed(2), p.revenue.week.toFixed(2), p.revenue.month.toFixed(2), p.revenue.year.toFixed(2),
        p.returned, p.damaged,
        p.is_negative ? 'Negative' : p.stock_quantity <= p.low_stock_threshold ? 'Low' : 'OK',
      ]),
      [],
      ['STOCK ALERTS'],
      ['Product', 'Category', 'Stock', 'Threshold', 'Status', 'Returned', 'Damaged'],
      ...data.low_stock_alerts.map(p => [
        p.name, p.category || '-', p.stock_quantity, p.low_stock_threshold,
        p.is_negative ? 'Negative' : 'Low',
        p.returned, p.damaged,
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `inventory_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const exportPDF = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>Inventory Report</title>
      <style>
        body{font-family:system-ui,sans-serif;padding:32px;color:#111;font-size:12px}
        h1{font-size:18px;margin-bottom:2px}
        h2{font-size:13px;margin:24px 0 8px;color:#374151;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
        table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px}
        th{background:#f8fafc;padding:6px 8px;text-align:left;color:#6b7280;text-transform:uppercase;font-size:10px}
        td{padding:6px 8px;border-top:1px solid #f1f5f9}
        .ok{color:#16a34a;font-weight:700} .low{color:#f59e0b;font-weight:700} .neg{color:#ef4444;font-weight:700}
        .subtitle{color:#6b7280;font-size:11px;margin-bottom:20px}
      </style></head><body>
      <h1>📊 Inventory Report</h1>
      <div class="subtitle">Generated ${new Date().toLocaleString()}</div>

      <h2>Sales Summary</h2>
      <table><tr><th>Period</th><th>Sales</th><th>Revenue (${currSym})</th></tr>
        <tr><td>Today</td><td>${data.sales_count.day}</td><td>${data.revenue.day.toFixed(2)}</td></tr>
        <tr><td>This Week</td><td>${data.sales_count.week}</td><td>${data.revenue.week.toFixed(2)}</td></tr>
        <tr><td>This Month</td><td>${data.sales_count.month}</td><td>${data.revenue.month.toFixed(2)}</td></tr>
        <tr><td>This Year</td><td>${data.sales_count.year}</td><td>${data.revenue.year.toFixed(2)}</td></tr>
      </table>

      <h2>All Products</h2>
      <table>
        <tr><th>Product</th><th>Category</th><th>Stock</th><th>Sold (${PERIOD_LABEL[period]})</th><th>Rev (${PERIOD_LABEL[period]})</th><th>Returned</th><th>Damaged</th><th>Status</th></tr>
        ${data.all_stock.map(p => `
          <tr>
            <td>${p.name}</td>
            <td>${p.category || '-'}</td>
            <td>${p.stock_quantity}</td>
            <td>${p.sold[period]}</td>
            <td>${currSym}${p.revenue[period].toFixed(2)}</td>
            <td>${p.returned}</td>
            <td>${p.damaged}</td>
            <td class="${p.is_negative ? 'neg' : p.stock_quantity <= p.low_stock_threshold ? 'low' : 'ok'}">
              ${p.is_negative ? 'Negative' : p.stock_quantity <= p.low_stock_threshold ? 'Low' : 'OK'}
            </td>
          </tr>`).join('')}
      </table>

      ${data.low_stock_alerts.length > 0 ? `
      <h2>Stock Alerts (${data.low_stock_alerts.length})</h2>
      <table>
        <tr><th>Product</th><th>Stock</th><th>Threshold</th><th>Returned</th><th>Damaged</th><th>Status</th></tr>
        ${data.low_stock_alerts.map(p => `
          <tr>
            <td>${p.name}</td>
            <td class="${p.is_negative ? 'neg' : 'low'}">${p.stock_quantity}</td>
            <td>${p.low_stock_threshold}</td>
            <td>${p.returned}</td>
            <td>${p.damaged}</td>
            <td class="${p.is_negative ? 'neg' : 'low'}">${p.is_negative ? 'Negative' : 'Low'}</td>
          </tr>`).join('')}
      </table>` : ''}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div style={{ 
      padding: 16, 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 20,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {logProduct && (
        <LogMovementModal
          product={logProduct}
          onClose={() => setLogProduct(null)}
          onDone={() => { setLogProduct(null); fetchData(); }}
        />
      )}

      {/* Period pills + Export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {PERIODS.map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: 'none', cursor: 'pointer',
              background: period === p ? '#6366f1' : '#f1f5f9',
              color: period === p ? '#fff' : '#64748b',
            }}>{PERIOD_LABEL[p]}</button>
          ))}
        </div>
        {perms.canExportExcel && (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={exportExcel} style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #86efac', background: '#f0fdf4', color: '#059669', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>📤 Excel</button>
          <button onClick={exportCSV} style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #93c5fd', background: '#eff6ff', color: '#3b82f6', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>📄 CSV</button>
          <button onClick={exportPDF} style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #c4b5fd', background: '#ede9fe', color: '#6366f1', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🖨 PDF</button>
        </div>
        )}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {[
          { label: 'Total Sales', value: data.sales_count[period], color: '#3b82f6' },
          { label: `Revenue (${currSym})`, value: data.revenue[period].toFixed(2), color: '#10b981' },
        ].map(card => (
          <div key={card.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>{card.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 4 }}>{PERIOD_LABEL[period]}</div>
          </div>
        ))}
      </div>

      {/* Top products */}
      {data.top_products.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Top Products — {PERIOD_LABEL[period]}</div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 480 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['#', 'Product', 'Sold', 'Revenue', 'Returned', 'Damaged'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: h === '#' || h === 'Product' ? 'left' : 'right', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.top_products.map((p, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', color: '#cbd5e1' }}>{i + 1}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e293b' }}>{p.name}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#3b82f6', fontWeight: 700 }}>{p.sold[period]}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#10b981', fontWeight: 700 }}>{currSym}{p.revenue[period].toFixed(2)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{p.returned}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#ef4444', fontWeight: 600 }}>{p.damaged}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All products table */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>All Products</div>
          <input
            placeholder="Search product / category..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 12, width: '100%', maxWidth: 200 }}
          />
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Product', 'Category', 'Stock', `Sold (${PERIOD_LABEL[period]})`, `Rev (${PERIOD_LABEL[period]})`, 'Returned', 'Damaged', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#94a3b8', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#cbd5e1', fontSize: 12 }}>No products found</td></tr>
              ) : filtered.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>{p.name}</td>
                  <td style={{ padding: '8px 12px', color: '#6b7280' }}>{p.category || '-'}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 700, color: p.is_negative ? '#ef4444' : p.stock_quantity <= p.low_stock_threshold ? '#f59e0b' : '#1e293b' }}>
                    {p.stock_quantity}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#3b82f6', fontWeight: 600 }}>{p.sold[period]}</td>
                  <td style={{ padding: '8px 12px', color: '#10b981', fontWeight: 600 }}>{currSym}{p.revenue[period].toFixed(2)}</td>
                  <td style={{ padding: '8px 12px', color: '#16a34a', fontWeight: 600 }}>{p.returned}</td>
                  <td style={{ padding: '8px 12px', color: '#ef4444', fontWeight: 600 }}>{p.damaged}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                      background: p.is_negative ? '#fef2f2' : p.stock_quantity <= p.low_stock_threshold ? '#fffbeb' : '#f0fdf4',
                      color: p.is_negative ? '#ef4444' : p.stock_quantity <= p.low_stock_threshold ? '#f59e0b' : '#16a34a',
                    }}>
                      {p.is_negative ? 'Negative' : p.stock_quantity <= p.low_stock_threshold ? 'Low' : 'OK'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {perms.canManageInventory && (
                    <button onClick={() => setLogProduct(p)} style={{
                      padding: '4px 10px', borderRadius: 6, border: '1.5px solid #e5e7eb',
                      background: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap',
                    }}>
                      + Log
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stock alerts */}
      {data.low_stock_alerts.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
            Stock Alerts <span style={{ color: '#ef4444' }}>({data.low_stock_alerts.length})</span>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 420 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Product', 'Stock', 'Threshold', 'Returned', 'Damaged'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Product' ? 'left' : 'right', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.low_stock_alerts.map(p => (
                  <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e293b' }}>{p.name}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: p.is_negative ? '#ef4444' : '#f59e0b' }}>{p.stock_quantity}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#94a3b8' }}>{p.low_stock_threshold}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{p.returned}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#ef4444', fontWeight: 600 }}>{p.damaged}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
